import type { ComputedRef } from 'vue';

import {
    type AuctionsListingResponseDto,
    type BackendErrorEntity,
    type DirectSalesListingResponseDto,
    type ErrorType,
    SellingMethod,
    useApiAuctionsListing,
    useApiDirectSalesListing,
} from '~/apiClient';
import { type PlatformSaleListingItemEntity } from '~/types/sale.type';

interface TotalItems {
    [SellingMethod.Auction]: number;
    [SellingMethod.DirectSale]: number;
    current: number;
}

export interface UseSlpDataReturn {
    suspense: () => Promise<unknown>;
    listingResponse: Ref<AuctionsListingResponseDto | DirectSalesListingResponseDto | undefined>;
    sales: Ref<PlatformSaleListingItemEntity[]>;
    totalItems: Ref<TotalItems>;
    isPending: Ref<boolean>;
    error: Ref<ErrorType<BackendErrorEntity> | null>;
    refetch: () => Promise<any>;
    appliedFiltersCounter: ComputedRef<number>;
    appliedFilters: ComputedRef<AppliedFilter[]>;
    hasAppliedFilters: ComputedRef<boolean>;
}

export const useSlpData = (): UseSlpDataReturn => {
    // COMPOSABLES
    const sellingMethod = useSlpSellingMethod();

    const {
        appliedFilters: alpAppliedFilters,
        appliedFiltersCounter: alpAppliedFiltersCounter,
        hasAppliedFilters: alpHasAppliedFilters,
        apiParams: alpApiParams,
    } = useSlpParams(SellingMethod.Auction);
    const {
        appliedFilters: dslpAppliedFilters,
        appliedFiltersCounter: dslpAppliedFiltersCounter,
        hasAppliedFilters: dslpHasAppliedFilters,
        apiParams: dslpApiParams,
    } = useSlpParams(SellingMethod.DirectSale);

    // DATA
    const appliedFilters = computed(() => (sellingMethod.value === SellingMethod.Auction ? alpAppliedFilters.value : dslpAppliedFilters.value));
    const appliedFiltersCounter = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpAppliedFiltersCounter.value;
            case SellingMethod.DirectSale:
                return dslpAppliedFiltersCounter.value;
        }
    });
    const hasAppliedFilters = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpHasAppliedFilters.value;
            case SellingMethod.DirectSale:
                return dslpHasAppliedFilters.value;
        }
    });

    // DATA FETCHING
    const auctionListingApiParams = computed(() => {
        if (sellingMethod.value !== SellingMethod.Auction) {
            return {
                ...mapDslpToAlpFiltersApiParams(dslpApiParams.value),
                itemsPerPage: 0,
            };
        }

        return alpApiParams.value;
    });
    const {
        data: alpData,
        suspense: alpSuspense,
        isPending: isAlpPending,
        error: alpError,
        refetch: alpRefetch,
    } = useApiAuctionsListing(auctionListingApiParams);

    const directSaleListingApiParams = computed(() => {
        if (sellingMethod.value !== SellingMethod.DirectSale) {
            return {
                ...mapAlpToDslpFiltersApiParams(alpApiParams.value),
                itemsPerPage: 0,
            };
        }

        return dslpApiParams.value;
    });
    const {
        data: dslpData,
        suspense: dslpSuspense,
        isPending: isDslpPending,
        error: dslpError,
        refetch: dslpRefetch,
    } = useApiDirectSalesListing(directSaleListingApiParams);

    const totalItems = computed<TotalItems>(() => {
        const dslpTotalItems = isDslpPending.value || !dslpData.value ? 0 : dslpData.value.totalItems;
        const alpTotalItems = isAlpPending.value || !alpData.value ? 0 : alpData.value.totalItems;

        return {
            [SellingMethod.Auction]: alpTotalItems,
            [SellingMethod.DirectSale]: dslpTotalItems,
            current: sellingMethod.value === SellingMethod.Auction ? alpTotalItems : dslpTotalItems,
        };
    });

    // Unmapped response of the listing currently in view. `sales` cannot stand in for it: it defaults
    // to an empty array, so it can never tell "the fetch brought nothing back" from "no results".
    const listingResponse = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpData.value;
            case SellingMethod.DirectSale:
                return dslpData.value;
        }
    });

    const sales = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpData.value?.auctions ?? [];
            case SellingMethod.DirectSale:
                return dslpData.value?.directSales ?? [];
        }
    });

    const isPending = computed(() => isAlpPending.value || isDslpPending.value);
    const error = computed(() => {
        switch (sellingMethod.value) {
            case SellingMethod.Auction:
                return alpError.value;
            case SellingMethod.DirectSale:
                return dslpError.value;
        }
    });

    return {
        appliedFilters,
        appliedFiltersCounter,
        hasAppliedFilters,
        listingResponse,
        sales,
        totalItems,
        isPending,
        error,
        refetch: async () => (sellingMethod.value === SellingMethod.Auction ? alpRefetch() : dslpRefetch()),
        suspense: async () => await Promise.all([alpSuspense(), dslpSuspense()]),
    };
};
